import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import TenantAdminLayout from '../Layout/TenantAdminLayout';
import { 
  FiPlus, 
  FiRefreshCw, 
  FiLayers, 
  FiBook, 
  FiCopy 
} from 'react-icons/fi';
import ProgramTypeForm from './ProgramTypeForm';
import ProgramForm from './ProgramForm';
import ProgramList from './ProgramList';
import ProgramSubjectMappingSection from './ProgramSubjectMappingSection';
import CopyProgramMapping from './CopyProgramMapping';
import { useProgramTypes } from './hooks/useProgramTypes';
import { usePrograms } from './hooks/usePrograms';

const Programs = () => {
  const location = useLocation();
  const { tenantId } = location.state || {};

  // Use hooks for program types and programs
  const {
    programTypes,
    loading: loadingTypes,
    error: errorTypes,
    fetchProgramTypes,
    createOrUpdateType,
    saving: savingType,
  } = useProgramTypes(tenantId);

  const {
    programs,
    loading: loadingPrograms,
    error: errorPrograms,
    fetchPrograms,
    createOrUpdateProgram,
    saving: savingProgram,
    updateProgramStatus,
  } = usePrograms(tenantId);

  // UI state
  const [editingType, setEditingType] = useState(null);
  const [isTypeFormOpen, setIsTypeFormOpen] = useState(false);

  const [editingProgram, setEditingProgram] = useState(null);
  const [isProgramFormOpen, setIsProgramFormOpen] = useState(false);

  const [activeTab, setActiveTab] = useState('programs');

  // Program Type Handlers
  const openCreateTypeModal = () => {
    setEditingType(null);
    setIsTypeFormOpen(true);
  };

  const startEditType = (type) => {
    setEditingType(type);
    setIsTypeFormOpen(true);
  };

  const closeTypeForm = () => {
    setEditingType(null);
    setIsTypeFormOpen(false);
  };

  const handleCreateOrUpdateType = async (form) => {
    await createOrUpdateType(form, editingType);
    closeTypeForm();
  };

  // Program Handlers
  const openCreateProgramModal = () => {
    setEditingProgram(null);
    setIsProgramFormOpen(true);
  };

  const startEditProgram = (program) => {
    setEditingProgram(program);
    setIsProgramFormOpen(true);
  };

  const closeProgramForm = () => {
    setEditingProgram(null);
    setIsProgramFormOpen(false);
  };

  const handleCreateOrUpdateProgram = async (form) => {
    await createOrUpdateProgram(form, editingProgram);
    closeProgramForm();
  };

  const handleProgramStatusChange = async (program, newStatus) => {
    await updateProgramStatus(program, newStatus);
    if (editingProgram && editingProgram._id === program._id) {
      closeProgramForm();
    }
  };

  // Error handling
  const error = errorTypes || errorPrograms;

  // Initial fetch
  useEffect(() => {
    if (tenantId) {
      fetchProgramTypes();
      fetchPrograms();
    }
  }, [tenantId, fetchProgramTypes, fetchPrograms]);

  return (
    <TenantAdminLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">
              Program Management
            </h1>
            <p className="mt-1 text-sm text-gray-600">
              Define program types, create programs, map subjects, and copy mappings across academic years.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => {
                fetchProgramTypes();
                fetchPrograms();
              }}
              disabled={loadingTypes || loadingPrograms}
              className="inline-flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-60"
            >
              <FiRefreshCw className="h-4 w-4" />
              Refresh
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('copy')}
              className="inline-flex items-center gap-2 px-4 py-2 border border-purple-200 rounded-lg text-sm font-medium text-purple-700 bg-purple-50 hover:bg-purple-100 shadow-sm transition-colors"
            >
              <FiCopy className="h-4 w-4" />
              Copy Mappings
            </button>
            <button
              type="button"
              onClick={openCreateProgramModal}
              className="inline-flex items-center gap-2 px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 shadow-sm"
            >
              <FiPlus className="h-4 w-4" />
              New Program
            </button>
          </div>
        </div>

        {/* Error Banner */}
        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            {error}
          </div>
        )}

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('programs')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'programs'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <FiBook className="inline w-4 h-4 mr-2" />
              Programs & Types
            </button>
            <button
              onClick={() => setActiveTab('mappings')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'mappings'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <FiLayers className="inline w-4 h-4 mr-2" />
              Subject Mapping
            </button>
            <button
              onClick={() => setActiveTab('copy')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'copy'
                  ? 'border-purple-500 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <FiCopy className="inline w-4 h-4 mr-2" />
              Copy Mappings
            </button>
          </nav>
        </div>

        {/* Content based on active tab */}
        {activeTab === 'programs' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left: Program Types */}
            <div className="lg:col-span-1 space-y-3">
              <div className="bg-white shadow-sm rounded-xl border border-gray-200">
                <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                  <h2 className="text-sm font-semibold text-gray-800">
                    Program Types
                  </h2>
                  <button
                    type="button"
                    onClick={openCreateTypeModal}
                    disabled={savingType}
                    className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-lg border border-indigo-200 text-indigo-700 bg-indigo-50 hover:bg-indigo-100 disabled:opacity-60"
                  >
                    + Add Type
                  </button>
                </div>

                {loadingTypes ? (
                  <div className="px-4 py-6 text-sm text-gray-500 text-center">
                    Loading program types...
                  </div>
                ) : programTypes.length === 0 ? (
                  <div className="px-4 py-6 text-sm text-gray-500 text-center">
                    No program types yet. Create one to start mapping programs.
                  </div>
                ) : (
                  <div className="max-h-96 overflow-y-auto">
                    <ul className="divide-y divide-gray-100">
                      {programTypes.map((t) => (
                        <li
                          key={t._id}
                          className="px-4 py-3 flex items-center justify-between gap-2 hover:bg-gray-50"
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-mono bg-slate-100 text-slate-800 border border-slate-200">
                                {t.code}
                              </span>
                              <span className="text-sm font-medium text-gray-900 truncate">
                                {t.name}
                              </span>
                            </div>
                            {t.description && (
                              <p className="text-xs text-gray-500 truncate">
                                {t.description}
                              </p>
                            )}
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <span
                              className={`inline-flex px-2 py-1 rounded-full text-[11px] ${
                                t.status === 'active'
                                  ? 'bg-green-100 text-green-700'
                                  : 'bg-gray-200 text-gray-700'
                              }`}
                            >
                              {t.status}
                            </span>
                            <button
                              type="button"
                              onClick={() => startEditType(t)}
                              disabled={savingType}
                              className="text-[11px] text-blue-600 hover:underline disabled:opacity-60"
                            >
                              Edit
                            </button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>

            {/* Right: Programs */}
            <div className="lg:col-span-2">
              {/* Optional: List all program codes at the top */}
              <div className="mb-2 flex flex-wrap gap-2">
                {programs.map((p) => (
                  <span
                    key={p._id}
                    className="inline-block px-2 py-1 rounded bg-gray-100 text-xs font-mono text-gray-700 border border-gray-200"
                  >
                    {p.code}
                  </span>
                ))}
              </div>
              <ProgramList
                loading={loadingPrograms}
                saving={savingProgram}
                programs={programs}
                programTypes={programTypes}
                onEdit={startEditProgram}
                onStatusChange={handleProgramStatusChange}
                onCreateClick={openCreateProgramModal}
              />
            </div>
          </div>
        )}

        {activeTab === 'mappings' && (
          <ProgramSubjectMappingSection
            tenantId={tenantId}
            programs={programs}
            programTypes={programTypes}
          />
        )}

        {activeTab === 'copy' && (
          <CopyProgramMapping
            tenantId={tenantId}
            programs={programs}
            programTypes={programTypes}
            onComplete={() => {
              // Refresh data if needed after copy
              fetchProgramTypes();
              fetchPrograms();
            }}
          />
        )}

        {/* Program Type Modal */}
        <ProgramTypeForm
          open={isTypeFormOpen}
          loading={savingType}
          editingType={editingType}
          onSubmit={handleCreateOrUpdateType}
          onClose={closeTypeForm}
        />

        {/* Program Modal */}
        <ProgramForm
          open={isProgramFormOpen}
          loading={savingProgram}
          editingProgram={editingProgram}
          programTypes={programTypes}
          onSubmit={handleCreateOrUpdateProgram}
          onClose={closeProgramForm}
        />
      </div>
    </TenantAdminLayout>
  );
};

export default Programs;